import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Stack,
  Chip,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

const FileUploadDialog = ({ open, onClose, onUpload, uploading = false }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: '',
    visibility: 'patient_doctor',
    isPatientVisible: true,
  });
  const [errors, setErrors] = useState({});

  // File validation
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/jpg',
    'image/gif',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx'];

  // Document types
  const DOCUMENT_TYPES = [
    { value: 'prescription', label: 'Prescription' },
    { value: 'lab_result', label: 'Lab Result' },
    { value: 'consultation_note', label: 'Consultation Note' },
    { value: 'imaging', label: 'Imaging Report' },
    { value: 'medical_certificate', label: 'Medical Certificate' },
    { value: 'vaccination_record', label: 'Vaccination Record' },
    { value: 'insurance_card', label: 'Insurance Card' },
    { value: 'identity_proof', label: 'Identity Proof' },
    { value: 'other', label: 'Other' },
  ];

  // Visibility options
  const VISIBILITY_OPTIONS = [
    { value: 'patient_only', label: 'Patient Only' },
    { value: 'patient_doctor', label: 'Patient & Doctor' },
    { value: 'patient_doctor_receptionist', label: 'Patient, Doctor & Receptionist' },
    { value: 'all_staff', label: 'All Staff' },
  ];

  // Dropzone configuration
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const error = rejectedFiles[0].errors[0];
      setErrors({ file: error.message });
      return;
    }

    const file = acceptedFiles[0];
    
    // Additional client-side validation
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const allowedExtensions = ALLOWED_EXTENSIONS.map(ext => ext.replace('.', ''));
    
    if (!allowedExtensions.includes(fileExtension)) {
      setErrors({ file: `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}` });
      return;
    }
    
    if (file.size > MAX_FILE_SIZE) {
      setErrors({ file: `File too large. Maximum size: ${MAX_FILE_SIZE / (1024 * 1024)}MB` });
      return;
    }
    
    setSelectedFile(file);
    setErrors({});

    // Auto-fill title if not provided
    if (!formData.title) {
      setFormData(prev => ({
        ...prev,
        title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      }));
    }
  }, [formData.title]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxSize: MAX_FILE_SIZE,
    multiple: false,
  });

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!selectedFile) {
      newErrors.file = 'Please select a file to upload';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.type) {
      newErrors.type = 'Document type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle upload
  const handleUpload = () => {
    if (!validateForm()) return;

    onUpload({
      file: selectedFile,
      ...formData,
    });
  };

  // Handle close
  const handleClose = () => {
    setSelectedFile(null);
    setFormData({
      title: '',
      description: '',
      type: '',
      visibility: 'patient_doctor',
      isPatientVisible: true,
    });
    setErrors({});
    onClose();
  };

  // Get file icon
  const getFileIcon = (file) => {
    if (!file) return <DescriptionIcon />;
    
    const mimeType = file.type;
    if (mimeType.includes('pdf')) return <PdfIcon />;
    if (mimeType.includes('image')) return <ImageIcon />;
    if (mimeType.includes('video')) return <VideoIcon />;
    if (mimeType.includes('zip') || mimeType.includes('rar')) return <ArchiveIcon />;
    return <DescriptionIcon />;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Upload Medical Document
          </Typography>
          <IconButton onClick={handleClose} disabled={uploading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* File Upload Area */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Select File
            </Typography>
            
            <Paper
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? 'primary.50' : 'background.paper',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.50',
                },
              }}
            >
              <input {...getInputProps()} />
              
              <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              
              {isDragActive ? (
                <Typography variant="h6" color="primary">
                  Drop the file here...
                </Typography>
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Drag & drop a file here, or click to select
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supported formats: PDF, JPG, PNG, GIF, DOC, DOCX (Max: 10MB)
                  </Typography>
                </Box>
              )}
            </Paper>

            {errors.file && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {errors.file}
              </Alert>
            )}
          </Box>

          {/* Selected File Info */}
          {selectedFile && (
            <Paper sx={{ p: 2, backgroundColor: 'background.default' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getFileIcon(selectedFile)}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(selectedFile.size)} • {selectedFile.type}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setSelectedFile(null)}
                  disabled={uploading}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          )}

          {/* Document Metadata */}
          <Stack spacing={2}>
            <Typography variant="subtitle1" gutterBottom>
              Document Information
            </Typography>

            <TextField
              label="Document Title"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              fullWidth
              error={!!errors.title}
              helperText={errors.title}
              disabled={uploading}
            />

            <TextField
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={uploading}
            />

            <FormControl fullWidth error={!!errors.type}>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleFormChange('type', e.target.value)}
                label="Document Type"
                disabled={uploading}
              >
                {DOCUMENT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
            </FormControl>

            <Divider />

            <Typography variant="subtitle1" gutterBottom>
              Access Control
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={formData.visibility}
                onChange={(e) => handleFormChange('visibility', e.target.value)}
                label="Visibility"
                disabled={uploading}
              >
                {VISIBILITY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SecurityIcon fontSize="small" />
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Control who can access this document
              </FormHelperText>
            </FormControl>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VisibilityIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Patient can view: {formData.isPatientVisible ? 'Yes' : 'No'}
              </Typography>
            </Box>
          </Stack>

          {/* Upload Progress */}
          {uploading && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Uploading document...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {/* Info Alert */}
          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2">
              <strong>Security Note:</strong> All uploaded documents are encrypted and stored securely. 
              Only authorized personnel can access documents based on the visibility settings you choose.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          startIcon={uploading ? null : <CloudUploadIcon />}
        >
          {uploading ? 'Uploading...' : 'Upload Document'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadDialog; 